/**
 * The app's identity inside the Gun layer, in one place.
 *
 * `src/shared/data/gun/*` is meant to be liftable into another project
 * unchanged — the value of a Gun layer is mostly the accumulated knowledge of
 * which Gun behaviours are traps, and that knowledge is app-agnostic. What is
 * NOT app-agnostic is three strings: the relay list, the graph root key, and
 * the local database name. Hardcoding them anywhere in the layer is what turns
 * "copy the folder" into "copy the folder and grep for the old app's name".
 *
 * All three read from the environment with a default, so a new project sets
 * them in `.env` and touches no source:
 *
 *   VITE_GUN_PEERS=https://relay.example.com/gun   (comma-separated)
 *   VITE_GUN_ROOT=myApp
 *   VITE_GUN_DB=my_app_graph
 *
 * Change `GUN_ROOT` or `GUN_DB` on an app that already has users and every
 * existing record moves out of reach — they name where the data lives, so treat
 * them as fixed once shipped.
 */

/** Public graph root: `gun.get(GUN_ROOT)`. See publicRoot. */
export const GUN_ROOT: string =
  (import.meta.env?.VITE_GUN_ROOT as string | undefined)?.trim() || 'thana';

/**
 * IndexedDB database backing the local graph store. See gunIDBAdapter.
 *
 * Bump this name whenever the envelope wire format changes under records that
 * are already stored: a new store name orphans the old one rather than leaving
 * unreadable records to be re-read and rejected forever.
 *
 * The default is set here, not only in `.env` — `.env` is gitignored, so a
 * build on any other machine would otherwise silently reopen the wrong store.
 *
 * ADR-0002 constraint 1: GUN_ROOT, GUN_DB_NAME and GUN_STORAGE_PREFIX name
 * WHERE DATA LIVES. Changing any of them after Thana has users puts every
 * existing record out of reach. They are fixed as of first release.
 */
export const GUN_DB_NAME: string =
  (import.meta.env?.VITE_GUN_DB as string | undefined)?.trim() || 'thana_graph_v1';

/** Fallback relay when `VITE_GUN_PEERS` is unset. See getPeers. */
/**
 * Thana ships NO default relay.
 *
 * ADR-0002 / N-08: the project operates no server for health data. Sync uses a
 * signed, refreshable list of community-run relays (architecture Section 8),
 * which arrives in v0.5. Until then this is empty and the layer is not reachable
 * from the app at all — v0.1 is local-only with no network code in the read or
 * write path.
 *
 * Shipping a default relay the project controlled would re-establish exactly
 * the controller status the architecture is designed to avoid (legal map
 * Section 9.4). This being empty is a deliberate legal position, not an
 * oversight.
 */
export const DEFAULT_PEERS: string[] = [];

/**
 * Prefix for every localStorage / IndexedDB key the layer writes (device id,
 * encrypted pair, replay marks, registration throttles). Like GUN_ROOT and
 * GUN_DB_NAME it names where existing data lives: fixed once shipped, set per
 * project via `VITE_GUN_PREFIX` before first release.
 */
export const GUN_STORAGE_PREFIX: string =
  (import.meta.env?.VITE_GUN_PREFIX as string | undefined)?.trim() || 'thana';

/** The layer's own localStorage keys, derived from {@link GUN_STORAGE_PREFIX}. */
export const GUN_STORAGE_KEYS = {
  DEVICE_ID: `${GUN_STORAGE_PREFIX}_device_id`,
  LAST_ALIAS: `${GUN_STORAGE_PREFIX}_last_alias`,
  REMEMBER_ME: `${GUN_STORAGE_PREFIX}_remember_me`,
  GUN_PAIR: `${GUN_STORAGE_PREFIX}_gun_pair`,
  // Random per-device salt used (with DEVICE_ID) to derive the key that
  // encrypts GUN_PAIR at rest. Device-level, not user-level.
  PAIR_SALT: `${GUN_STORAGE_PREFIX}_pair_salt`,
  ACTIVE_USER: `${GUN_STORAGE_PREFIX}_active_user_pub`,
} as const;
